import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Boxes, CheckCircle2, Snowflake } from 'lucide-react';
import { fetchPowerBIFabricSqlData, fetchPowerBISemanticData } from '../services/powerbiService';
import authService from '../services/authService';
import UploadMappingWizard from '../components/UploadMappingWizard';
import '../modules/webv2/webV2Module.css';

const WORKSPACE_ID = '7d441575-4aa9-4260-99dd-543f002c0340';
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
const DEFAULT_COLD_CHAIN_GAPS_DAX_QUERY = `
EVALUATE
SUMMARIZECOLUMNS(
  'cce_gap_data'[country_name],
  'cce_gap_data'[iso3],
  "stores_assessed", COUNTROWS('cce_gap_data'),
  "population", SUM('cce_gap_data'[population]),
  "projected_population", SUM('cce_gap_data'[projected_population]),
  "total_net_storage_gap_in_l", SUMX(FILTER('cce_gap_data', 'cce_gap_data'[total_net_storage_gap_in_l] > 0), 'cce_gap_data'[total_net_storage_gap_in_l]),
  "total_net_freezing_gap_in_l", SUMX(FILTER('cce_gap_data', 'cce_gap_data'[total_net_freezing_gap_in_l] > 0), 'cce_gap_data'[total_net_freezing_gap_in_l]),
  "total_ucc_gap_in_l", SUMX(FILTER('cce_gap_data', 'cce_gap_data'[total_ucc_gap_in_l] > 0), 'cce_gap_data'[total_ucc_gap_in_l])
)
`;
const COLD_CHAIN_GAPS_DATASET_ID = process.env.REACT_APP_COLD_CHAIN_GAPS_DATASET_ID || '266c74cc-354a-4bfe-b29d-edb9961d6e36';
const COLD_CHAIN_GAPS_WORKSPACE_ID = process.env.REACT_APP_COLD_CHAIN_GAPS_WORKSPACE_ID || WORKSPACE_ID;
const COLD_CHAIN_GAPS_TABLE = process.env.REACT_APP_COLD_CHAIN_GAPS_TABLE_NAME || 'cce_gap_data';
const COLD_CHAIN_GAPS_DAX_QUERY = process.env.REACT_APP_COLD_CHAIN_GAPS_DAX_QUERY || DEFAULT_COLD_CHAIN_GAPS_DAX_QUERY;
const UCC_INVENTORY_TABLE = process.env.REACT_APP_UCC_INVENTORY_TABLE_NAME || 'dbo.Query';
const GAVI_INVENTORY_TABLE = process.env.REACT_APP_GAVI_INVENTORY_TABLE_NAME || 'gavi_inventory_data';

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').toLowerCase();
};

const findColumnKey = (columns, exactNames, partialNames = []) => {
  const exact = columns.find((column) => exactNames.includes(normalizeColumn(column)));
  if (exact) return exact;
  return columns.find((column) => partialNames.some((name) => normalizeColumn(column).includes(name))) || null;
};

const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');

const parseNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).replace(/[,%\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const sumColumn = (rows, columnKey, fallbackToRowCount = false) => {
  if (!columnKey) return fallbackToRowCount ? rows.length : 0;
  const values = rows.map((row) => parseNumber(row?.[columnKey])).filter((value) => value !== null);
  if (values.length === 0) return fallbackToRowCount ? rows.length : 0;
  return values.reduce((sum, value) => sum + value, 0);
};

const sumPositiveColumn = (rows, columnKey) => rows.reduce((sum, row) => {
  const value = parseNumber(row?.[columnKey]);
  return value && value > 0 ? sum + value : sum;
}, 0);

const formatNumber = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const formatLitres = (value) => `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} L`;
const normalizeMatchValue = (value) => String(value || '').trim().toLowerCase();
const normalizeIso3 = (value) => String(value || '').trim().toUpperCase();
const noDataPill = <span className="cold-chain-status is-empty">No data</span>;
const renderLitres = (value, hasData = true) => (hasData ? formatLitres(value) : noDataPill);
const hasNumericColumnData = (rows, columnKey) => Boolean(columnKey) && rows.some((row) => parseNumber(row?.[columnKey]) !== null);
const formatYear = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 4);
  return String(parsed.getFullYear());
};
const renderFunctionalEquipment = (functional, total, hasData = true) => (
  hasData && total > 0 ? `${((Number(functional || 0) / Number(total)) * 100).toFixed(1)}%` : noDataPill
);
const renderYear = (value) => (value ? formatYear(value) : noDataPill);

const loadSemanticTable = async ({ datasetId, workspaceId, query, tableName }) => {
  const effectiveQuery = String(query || '').trim() || undefined;
  const effectiveTableName = effectiveQuery ? undefined : (String(tableName || '').trim() || undefined);
  return fetchPowerBISemanticData(datasetId, workspaceId, effectiveQuery, 5000, effectiveTableName);
};

const buildCountryKey = (row, iso3Column, countryColumn) => {
  const iso3 = getRowText(row, iso3Column).toUpperCase();
  const country = getRowText(row, countryColumn);
  return iso3 || country.toLowerCase() || 'unknown';
};

const getCountryLabel = (country, columnMap) => (
  country.uccRows.map((row) => getRowText(row, columnMap.uccCountryColumn)).find(Boolean) ||
  country.gapRows.map((row) => getRowText(row, columnMap.gapCountryColumn)).find(Boolean) ||
  country.uccRows.map((row) => getRowText(row, columnMap.uccIso3Column).toUpperCase()).find(Boolean) ||
  country.gapRows.map((row) => getRowText(row, columnMap.gapIso3Column).toUpperCase()).find(Boolean) ||
  country.key
);

const getCountryIso3 = (country, columnMap) => (
  country.uccRows.map((row) => getRowText(row, columnMap.uccIso3Column).toUpperCase()).find(Boolean) ||
  country.gapRows.map((row) => getRowText(row, columnMap.gapIso3Column).toUpperCase()).find(Boolean) ||
  (/^[A-Z]{3}$/.test(country.key) ? country.key : '')
);

const getStatus = (gapCount, inventoryCount) => {
  if (gapCount > 0 && inventoryCount > 0) return { label: 'Gap with inventory', className: 'is-warning' };
  if (gapCount > 0) return { label: 'Gap identified', className: 'is-gap' };
  if (inventoryCount > 0) return { label: 'Inventory recorded', className: 'is-ready' };
  return { label: 'No data', className: 'is-empty' };
};

const ColdChainInventoryGaps = () => {
  const [gapRows, setGapRows] = useState([]);
  const [gapColumns, setGapColumns] = useState([]);
  const [uccRows, setUccRows] = useState([]);
  const [uccColumns, setUccColumns] = useState([]);
  const [cceRows, setCceRows] = useState([]);
  const [elmisRows, setElmisRows] = useState([]);
  const [isElmisLoading, setIsElmisLoading] = useState(true);
  const [elmisError, setElmisError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCountryKey, setSelectedCountryKey] = useState('');
  const [showAllInventoryCountries, setShowAllInventoryCountries] = useState(false);
  const [activeInventoryTab, setActiveInventoryTab] = useState('ucc-inventory');

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setIsLoading(true);
      setError('');

      try {
        let gapResult = null;
        let uccResult = null;
        let cceResult = null;
        const loadErrors = [];

        try {
          gapResult = await loadSemanticTable({
            datasetId: COLD_CHAIN_GAPS_DATASET_ID,
            workspaceId: COLD_CHAIN_GAPS_WORKSPACE_ID,
            query: COLD_CHAIN_GAPS_DAX_QUERY,
            tableName: COLD_CHAIN_GAPS_TABLE
          });
        } catch (gapError) {
          loadErrors.push(`Cold chain gaps: ${gapError?.message || 'Unavailable'}`);
        }

        try {
          uccResult = await fetchPowerBIFabricSqlData({
            tableName: UCC_INVENTORY_TABLE,
            maxRows: 5000
          });
        } catch (uccError) {
          loadErrors.push(`UCC inventory: ${uccError?.message || 'Unavailable'}`);
        }

        try {
          const idToken = await authService.getIdToken();
          const response = await fetch(`${API_BASE}/db-query`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`
            },
            body: JSON.stringify({
              query: `
                SELECT
                  iso3,
                  SUM(
                    COALESCE(capacity_plus_5c, 0) +
                    COALESCE(capacity_minus_20c, 0) +
                    COALESCE(capacity_minus_70c, 0)
                  ) AS cce_inventory_l,
                  COUNT(*) AS cce_inventory_records,
                  SUM(CASE WHEN LOWER(TRIM(operational_status)) = 'functional' THEN 1 ELSE 0 END) AS functional_equipment_count,
                  MAX(date_of_inventory) AS date_of_report
                FROM ${GAVI_INVENTORY_TABLE}
                GROUP BY iso3;
              `
            }),
            mode: 'cors'
          });

          if (!response.ok) {
            throw new Error(`CCE inventory request failed (${response.status})`);
          }

          cceResult = await response.json();
        } catch (cceError) {
          loadErrors.push(`CCE inventory: ${cceError?.message || 'Unavailable'}`);
        }

        if (!mounted) return;
        const nextGapRows = Array.isArray(gapResult?.data) ? gapResult.data : [];
        const nextUccRows = Array.isArray(uccResult?.data) ? uccResult.data : [];
        const nextCceRows = Array.isArray(cceResult?.result) ? cceResult.result : (Array.isArray(cceResult) ? cceResult : []);
        setGapRows(nextGapRows);
        setGapColumns(Array.isArray(gapResult?.columns) ? gapResult.columns : []);
        setUccRows(nextUccRows);
        setUccColumns(Array.isArray(uccResult?.columns) ? uccResult.columns : []);
        setCceRows(nextCceRows);
        setError(loadErrors.length > 0 ? loadErrors.join(' | ') : '');
      } catch (loadError) {
        if (!mounted) return;
        setGapRows([]);
        setGapColumns([]);
        setUccRows([]);
        setUccColumns([]);
        setCceRows([]);
        setError(loadError?.message || 'Cold chain semantic model data is unavailable right now.');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadElmisMappings = async () => {
      setIsElmisLoading(true);
      setElmisError('');

      try {
        const idToken = await authService.getIdToken();
        const response = await fetch(`${API_BASE}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            query: `
              SELECT
                country,
                iso3,
                vax_lmis,
                vax_tss,
                other_lmis,
                other_donor,
                other_tss,
                dhis2_natl
              FROM elmis_country_mapping;
            `
          }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`eLMIS mapping request failed (${response.status})`);
        }

        const payload = await response.json();
        if (!mounted) return;
        setElmisRows(Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []));
      } catch (loadError) {
        if (!mounted) return;
        setElmisRows([]);
        setElmisError('eLMIS country mapping is unavailable right now.');
      } finally {
        if (mounted) setIsElmisLoading(false);
      }
    };

    loadElmisMappings();

    return () => {
      mounted = false;
    };
  }, []);

  const columnMap = useMemo(() => {
    const gapIso3Column = findColumnKey(gapColumns, ['iso3', 'country iso3', 'country code'], ['iso3']);
    const gapCountryColumn = findColumnKey(gapColumns, ['country', 'country name'], ['country']);
    const storageGapColumn = findColumnKey(gapColumns, ['total net storage gap in l', 'net storage gap in l', 'storage gap in l'], ['storage gap']);
    const freezingGapColumn = findColumnKey(gapColumns, ['total net freezing gap in l', 'net freezing gap in l', 'freezing gap in l'], ['freezing gap']);
    const uccGapColumn = findColumnKey(gapColumns, ['total ucc gap in l', 'ucc gap in l'], ['ucc gap']);
    const populationColumn = findColumnKey(gapColumns, ['population'], ['population']);
    const projectedPopulationColumn = findColumnKey(gapColumns, ['projected population'], ['projected population']);
    const storeCountColumn = findColumnKey(gapColumns, ['stores assessed', 'store count', 'stores'], ['stores assessed', 'store count']);
    const provinceColumn = findColumnKey(gapColumns, ['province'], ['province']);
    const districtColumn = findColumnKey(gapColumns, ['district'], ['district']);
    const storeColumn = findColumnKey(gapColumns, ['store name'], ['store']);
    const levelColumn = findColumnKey(gapColumns, ['sc level'], ['level']);
    const electricityColumn = findColumnKey(gapColumns, ['electricity'], ['electricity']);
    const gapEquipmentColumn = findColumnKey(gapColumns, ['equipment type', 'device type', 'pqs category', 'category'], ['equipment', 'device', 'category']);

    const uccIso3Column = findColumnKey(uccColumns, ['iso3', 'country iso3', 'country code'], ['iso3']);
    const uccCountryColumn = findColumnKey(uccColumns, ['country', 'country name'], ['country']);
    const uccCapacityColumn = findColumnKey(uccColumns, ['capacity (l)', 'capacity l', 'capacity'], ['capacity']);
    const uccEquipmentColumn = findColumnKey(uccColumns, ['equipment type', 'device type', 'pqs category', 'category', 'model'], ['equipment', 'device', 'category', 'model']);

    return {
      gapIso3Column,
      gapCountryColumn,
      storageGapColumn,
      freezingGapColumn,
      uccGapColumn,
      populationColumn,
      projectedPopulationColumn,
      storeCountColumn,
      provinceColumn,
      districtColumn,
      storeColumn,
      levelColumn,
      electricityColumn,
      gapEquipmentColumn,
      uccIso3Column,
      uccCountryColumn,
      uccCapacityColumn,
      uccEquipmentColumn
    };
  }, [gapColumns, uccColumns]);

  const countryRows = useMemo(() => {
    const grouped = new Map();

    gapRows.forEach((row) => {
      const key = buildCountryKey(row, columnMap.gapIso3Column, columnMap.gapCountryColumn);
      const current = grouped.get(key) || { key, gapRows: [], uccRows: [], cceRows: [] };
      current.gapRows.push(row);
      grouped.set(key, current);
    });

    uccRows.forEach((row) => {
      const key = buildCountryKey(row, columnMap.uccIso3Column, columnMap.uccCountryColumn);
      const current = grouped.get(key) || { key, gapRows: [], uccRows: [], cceRows: [] };
      current.uccRows.push(row);
      grouped.set(key, current);
    });

    cceRows.forEach((row) => {
      const key = buildCountryKey(row, 'iso3', null);
      const current = grouped.get(key) || { key, gapRows: [], uccRows: [], cceRows: [] };
      current.cceRows = current.cceRows || [];
      current.cceRows.push(row);
      grouped.set(key, current);
    });

    return Array.from(grouped.values())
      .map((country) => {
        const countryCceRows = country.cceRows || [];
        const inventoryCount = sumColumn(country.uccRows, columnMap.uccCapacityColumn);
        const cceInventory = sumColumn(countryCceRows, 'cce_inventory_l');
        const cceEquipmentCount = sumColumn(countryCceRows, 'cce_inventory_records');
        const functionalEquipmentCount = sumColumn(countryCceRows, 'functional_equipment_count');
        const hasUccInventoryData = hasNumericColumnData(country.uccRows, columnMap.uccCapacityColumn);
        const hasCceInventoryData = hasNumericColumnData(countryCceRows, 'cce_inventory_l');
        const hasCceEquipmentData = hasNumericColumnData(countryCceRows, 'cce_inventory_records');
        const hasStorageGapData = hasNumericColumnData(country.gapRows, columnMap.storageGapColumn);
        const hasFreezingGapData = hasNumericColumnData(country.gapRows, columnMap.freezingGapColumn);
        const hasUccGapData = hasNumericColumnData(country.gapRows, columnMap.uccGapColumn);
        const reportDates = countryCceRows.map((row) => row?.date_of_report).filter(Boolean).sort();
        const dateOfReport = reportDates[reportDates.length - 1] || '';
        const totalCountryInventory = inventoryCount + cceInventory;
        const storageGap = sumPositiveColumn(country.gapRows, columnMap.storageGapColumn);
        const freezingGap = sumPositiveColumn(country.gapRows, columnMap.freezingGapColumn);
        const uccGap = sumPositiveColumn(country.gapRows, columnMap.uccGapColumn);
        const gapCount = storageGap + freezingGap + uccGap;
        const population = sumColumn(country.gapRows, columnMap.populationColumn);
        const projectedPopulation = sumColumn(country.gapRows, columnMap.projectedPopulationColumn);
        const storeCount = sumColumn(country.gapRows, columnMap.storeCountColumn, true);
        const status = getStatus(gapCount, totalCountryInventory);
        const equipmentTypes = new Set([
          ...country.uccRows.map((row) => getRowText(row, columnMap.uccEquipmentColumn)),
          ...country.gapRows.map((row) => getRowText(row, columnMap.gapEquipmentColumn))
        ].filter(Boolean));

        return {
          ...country,
          countryName: getCountryLabel(country, columnMap),
          iso3: getCountryIso3(country, columnMap),
          inventoryCount,
          cceInventory,
          cceEquipmentCount,
          functionalEquipmentCount,
          hasUccInventoryData,
          hasCceInventoryData,
          hasCceEquipmentData,
          hasStorageGapData,
          hasFreezingGapData,
          hasUccGapData,
          dateOfReport,
          gapCount,
          storageGap,
          freezingGap,
          uccGap,
          population,
          projectedPopulation,
          storeCount,
          equipmentTypeCount: equipmentTypes.size,
          status
        };
      })
      .sort((a, b) => b.gapCount - a.gapCount || a.countryName.localeCompare(b.countryName));
  }, [cceRows, columnMap, gapRows, uccRows]);

  useEffect(() => {
    if (selectedCountryKey || countryRows.length === 0) return;
    setSelectedCountryKey(countryRows[0].key);
  }, [countryRows, selectedCountryKey]);

  const selectedCountry = countryRows.find((country) => country.key === selectedCountryKey) || countryRows[0] || null;
  const countriesWithGaps = countryRows.filter((country) => country.gapCount > 0).length;
  const totalInventory = countryRows.reduce((sum, country) => sum + country.inventoryCount, 0);
  const totalCceInventory = countryRows.reduce((sum, country) => sum + country.cceInventory, 0);
  const hasAnyUccInventoryData = countryRows.some((country) => country.hasUccInventoryData);
  const hasAnyCceInventoryData = countryRows.some((country) => country.hasCceInventoryData);
  const totalGaps = countryRows.reduce((sum, country) => sum + country.gapCount, 0);
  const gapRate = totalInventory + totalGaps > 0 ? (totalGaps / (totalInventory + totalGaps)) * 100 : null;
  const displayedCountryRows = showAllInventoryCountries ? countryRows : countryRows.slice(0, 8);

  const selectedInventoryRows = selectedCountry?.uccRows?.slice(0, 12) || [];
  const selectedGapRows = selectedCountry?.gapRows?.slice(0, 12) || [];
  const selectedElmisMapping = useMemo(() => {
    if (!selectedCountry || elmisRows.length === 0) return null;

    const selectedIso3 = normalizeIso3(selectedCountry.iso3 || selectedCountry.key);
    const selectedCountryName = normalizeMatchValue(selectedCountry.countryName);

    return elmisRows.find((row) => {
      const rowIso3 = normalizeIso3(row?.iso3);
      const rowCountry = normalizeMatchValue(row?.country);
      return (selectedIso3 && rowIso3 === selectedIso3) || (selectedCountryName && rowCountry === selectedCountryName);
    }) || null;
  }, [elmisRows, selectedCountry]);

  return (
    <section className="home-section vaccines-page-section vaccines-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <h2 className="vaccines-dashboard-title">Cold chain inventory & gaps</h2>
          <p className="vaccines-dashboard-subtitle">
            Single country view combining UCC inventory records from Fabric SQL with cold chain gap data from the Power BI semantic model.
          </p>
        </div>
      </div>

      <div className="vaccines-kpi-row">
        {[
          { key: 'countries', icon: Snowflake, value: formatNumber(countryRows.length), label: 'Countries with data', meta: 'Inventory or gap records' },
          {
            key: 'inventory',
            icon: Boxes,
            value: renderLitres(totalInventory, hasAnyUccInventoryData),
            label: 'Current inventory',
            meta: <>CCE: {renderLitres(totalCceInventory, hasAnyCceInventoryData)}</>
          },
          { key: 'gaps', icon: AlertTriangle, value: formatNumber(totalGaps), label: 'Estimated gap', meta: `${formatNumber(countriesWithGaps)} countries with gaps` },
          { key: 'rate', icon: CheckCircle2, value: gapRate === null ? 'N/A' : `${gapRate.toFixed(1)}%`, label: 'Gap share', meta: 'Gap / inventory plus gap' }
        ].map((item) => {
          const Icon = item.icon;
          return (
            <article key={item.key} className={`vaccines-kpi-card vaccines-kpi-${item.key}`}>
              <span className="vaccines-kpi-icon" aria-hidden="true">
                <Icon />
              </span>
              <div>
                <p className="vaccines-kpi-value">{isLoading ? '...' : item.value}</p>
                <p className="vaccines-kpi-label">{item.label}</p>
                <p className="vaccines-kpi-meta">{item.meta}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="vaccines-main-grid">
        <div className="vaccines-left-column">
          <div className="vaccines-schedule-card vaccines-matrix-card">
            <div className="vaccines-card-header">
              <div>
                <p className="vaccines-section-label">Country x Cold Chain Matrix</p>
                <h3 className="vaccines-card-title">Current inventory and cold chain gap</h3>
              </div>
              <div className="vaccines-matrix-legend" aria-label="Cold chain status legend">
                <span><i className="matrix-status is-routine" />Inventory recorded</span>
                <span><i className="matrix-status is-hpv-follow-up" />Gap with inventory</span>
                <span><i className="matrix-status is-not-ri" />Gap identified</span>
              </div>
            </div>

            <div className="vaccines-wuenic-table-wrap">
              <table className="vaccines-wuenic-table" aria-label="Cold chain inventory and gaps by country">
                <thead>
                  <tr>
                    <th scope="col">Country</th>
                    <th scope="col">CCE Inventory (L)</th>
                    <th scope="col">UCC Inventory (L)</th>
                    <th scope="col">Storage Gap</th>
                    <th scope="col">Stores</th>
                    <th scope="col">Functional Equipment</th>
                    <th scope="col">Report Year</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={7}>Loading cold chain data...</td>
                    </tr>
                  )}
                  {!isLoading && error && countryRows.length === 0 && (
                    <tr>
                      <td colSpan={7}>{error}</td>
                    </tr>
                  )}
                  {!isLoading && !error && countryRows.length === 0 && (
                    <tr>
                      <td colSpan={7}>No country rows returned from the configured data sources.</td>
                    </tr>
                  )}
                  {!isLoading && displayedCountryRows.map((country) => (
                    <tr
                      key={country.key}
                      className={country.key === selectedCountryKey ? 'is-selected' : ''}
                      aria-selected={country.key === selectedCountryKey}
                    >
                      <th scope="row">
                        <button type="button" onClick={() => setSelectedCountryKey(country.key)}>
                          {country.countryName}
                        </button>
                      </th>
                      <td>{renderLitres(country.cceInventory, country.hasCceInventoryData)}</td>
                      <td>{renderLitres(country.inventoryCount, country.hasUccInventoryData)}</td>
                      <td>{renderLitres(country.storageGap, country.hasStorageGapData)}</td>
                      <td>{formatNumber(country.storeCount)}</td>
                      <td>{renderFunctionalEquipment(country.functionalEquipmentCount, country.cceEquipmentCount, country.hasCceEquipmentData)}</td>
                      <td>{renderYear(country.dateOfReport)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!isLoading && error && countryRows.length > 0 && (
              <p className="vaccines-table-footnote">{error}</p>
            )}
            {!isLoading && countryRows.length > 8 && (
              <div className="vaccines-matrix-footer">
                <p className="vaccines-footnote">
                  Showing {displayedCountryRows.length} of {countryRows.length} countries in this view.
                </p>
                <button
                  type="button"
                  className="vaccines-expand-button"
                  onClick={() => setShowAllInventoryCountries((current) => !current)}
                >
                  {showAllInventoryCountries ? 'Show fewer countries' : `Show all ${countryRows.length} countries`}
                </button>
              </div>
            )}
          </div>
        </div>

        <aside className="vaccines-detail-panel" aria-label="Country cold chain details">
          <div className="vaccines-country-header">
            <div>
              <p className="vaccines-section-label">Country snapshot</p>
              <h3>{selectedCountry?.countryName || 'No country selected'}</h3>
            </div>
            {selectedCountry && (
              <span className={`country-badge ${selectedCountry.gapCount > 0 ? 'is-gavi' : 'is-non-gavi'}`}>
                {selectedCountry.status.label}
              </span>
            )}
          </div>

          <div className="vaccines-snapshot-grid">
            <div><strong>{renderLitres(selectedCountry?.cceInventory, selectedCountry?.hasCceInventoryData)}</strong><span>CCE inventory</span></div>
            <div><strong>{renderLitres(selectedCountry?.inventoryCount, selectedCountry?.hasUccInventoryData)}</strong><span>UCC inventory</span></div>
            <div><strong>{renderLitres(selectedCountry?.storageGap, selectedCountry?.hasStorageGapData)}</strong><span>Storage gap</span></div>
            <div><strong>{renderLitres(selectedCountry?.freezingGap, selectedCountry?.hasFreezingGapData)}</strong><span>Freezing gap</span></div>
            <div><strong>{renderLitres(selectedCountry?.uccGap, selectedCountry?.hasUccGapData)}</strong><span>UCC gap</span></div>
            <div><strong>{formatNumber(selectedCountry?.storeCount)}</strong><span>Stores assessed</span></div>
            <div><strong>{formatNumber(selectedCountry?.projectedPopulation || selectedCountry?.population)}</strong><span>Population</span></div>
          </div>

          <div className="vaccines-panel-section">
            <h4>LMIS and reporting systems</h4>
            {isElmisLoading && <p className="vaccines-table-footnote">Loading eLMIS country mapping...</p>}
            {!isElmisLoading && elmisError && <p className="vaccines-table-footnote">{elmisError}</p>}
            {!isElmisLoading && !elmisError && !selectedElmisMapping && (
              <p className="vaccines-table-footnote">No eLMIS country mapping found for this country.</p>
            )}
            {!isElmisLoading && !elmisError && selectedElmisMapping && (
              <div className="vaccines-snapshot-grid">
                <div><strong>{selectedElmisMapping.iso3 || 'N/A'}</strong><span>ISO3</span></div>
                <div><strong>{selectedElmisMapping.vax_lmis || 'N/A'}</strong><span>Vaccine LMIS</span></div>
                <div><strong>{selectedElmisMapping.vax_tss || 'N/A'}</strong><span>Vaccine TSS</span></div>
                <div><strong>{selectedElmisMapping.other_lmis || 'N/A'}</strong><span>Other LMIS</span></div>
                <div><strong>{selectedElmisMapping.other_donor || 'N/A'}</strong><span>Other donor</span></div>
                <div><strong>{selectedElmisMapping.other_tss || 'N/A'}</strong><span>Other TSS</span></div>
                <div><strong>{selectedElmisMapping.dhis2_natl || 'N/A'}</strong><span>DHIS2 national</span></div>
              </div>
            )}
          </div>

          <div className="vaccines-panel-section">
            <div className="vaccines-card-header">
              <div>
                <p className="vaccines-section-label">Inventory</p>
                <h4>Inventory records</h4>
              </div>
              <nav className="flex gap-2" aria-label="Inventory tabs">
                <button
                  type="button"
                  className={`px-3 py-2 text-xs font-bold rounded-full border ${activeInventoryTab === 'ucc-inventory' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-slate-500 border-slate-200 hover:text-blue-700'}`}
                  onClick={() => setActiveInventoryTab('ucc-inventory')}
                >
                  UCC inventory
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 text-xs font-bold rounded-full border ${activeInventoryTab === 'upload-mapping' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-slate-500 border-slate-200 hover:text-blue-700'}`}
                  onClick={() => setActiveInventoryTab('upload-mapping')}
                >
                  Upload & Mapping
                </button>
              </nav>
            </div>

            {activeInventoryTab === 'ucc-inventory' && (
              <div className="vaccines-wuenic-table-wrap">
                <table className="vaccines-wuenic-table" aria-label="Selected country UCC inventory records">
                  <thead>
                    <tr>
                      <th scope="col">Equipment</th>
                      <th scope="col">Capacity (L)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInventoryRows.length === 0 && (
                      <tr>
                        <td colSpan={2}>No UCC inventory rows for this country.</td>
                      </tr>
                    )}
                    {selectedInventoryRows.map((row, index) => (
                      <tr key={`inventory-${selectedCountry?.key}-${index}`}>
                        <td>{getRowText(row, columnMap.uccEquipmentColumn) || 'Inventory item'}</td>
                        <td>{renderLitres(parseNumber(row?.[columnMap.uccCapacityColumn]), parseNumber(row?.[columnMap.uccCapacityColumn]) !== null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeInventoryTab === 'upload-mapping' && (
              <div className="mt-3">
                <UploadMappingWizard />
              </div>
            )}
          </div>

          <div className="vaccines-panel-section">
            <h4>Gap records by store</h4>
            <div className="vaccines-wuenic-table-wrap">
              <table className="vaccines-wuenic-table" aria-label="Selected country cold chain gap records">
                <thead>
                  <tr>
                    <th scope="col">Store</th>
                    <th scope="col">Location</th>
                    <th scope="col">Level</th>
                    <th scope="col">Electricity</th>
                    <th scope="col">Storage Gap</th>
                    <th scope="col">Freezing Gap</th>
                    <th scope="col">UCC Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedGapRows.length === 0 && (
                    <tr>
                      <td colSpan={7}>No cold chain gap rows for this country.</td>
                    </tr>
                  )}
                  {selectedGapRows.map((row, index) => (
                    <tr key={`gap-${selectedCountry?.key}-${index}`}>
                      <td>{getRowText(row, columnMap.storeColumn) || 'N/A'}</td>
                      <td>{[getRowText(row, columnMap.provinceColumn), getRowText(row, columnMap.districtColumn)].filter(Boolean).join(' / ') || 'N/A'}</td>
                      <td>{getRowText(row, columnMap.levelColumn) || 'N/A'}</td>
                      <td>{getRowText(row, columnMap.electricityColumn) || 'N/A'}</td>
                      <td>{renderLitres(parseNumber(row?.[columnMap.storageGapColumn]), parseNumber(row?.[columnMap.storageGapColumn]) !== null)}</td>
                      <td>{renderLitres(parseNumber(row?.[columnMap.freezingGapColumn]), parseNumber(row?.[columnMap.freezingGapColumn]) !== null)}</td>
                      <td>{renderLitres(parseNumber(row?.[columnMap.uccGapColumn]), parseNumber(row?.[columnMap.uccGapColumn]) !== null)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="vaccines-table-footnote">
            Data sources: `{COLD_CHAIN_GAPS_TABLE}` semantic model table, `{UCC_INVENTORY_TABLE}` Fabric SQL table, and `{GAVI_INVENTORY_TABLE}` database table.
          </p>
        </aside>
      </div>
    </section>
  );
};

export default ColdChainInventoryGaps;
